import type { QuoteDocument, QuoteStatus } from '@/modules/quotes/types'
import { customerService } from '@/modules/customers/services'

import {
  addDaysIso,
  CRM_KEYS,
  daysBetween,
  nowIso,
  readCrmJson,
  writeCrmJson,
} from './storage'
import type {
  LostReason,
  LostReasonCode,
  Opportunity,
  OpportunityFilters,
  OpportunityStage,
  TimelineEvent,
} from './types'
import {
  DEFAULT_LOST_REASONS,
  STAGE_LABELS,
  STAGE_PROBABILITY,
} from './types'

function mapQuoteStatusToStage(status: QuoteStatus): OpportunityStage {
  switch (status) {
    case 'DRAFT':
      return 'DRAFT'
    case 'SENT':
      return 'SENT'
    case 'AWAITING':
      return 'AWAITING'
    case 'APPROVED':
      return 'APPROVED'
    case 'LOST':
      return 'LOST'
    case 'CANCELLED':
      return 'CANCELLED'
    default:
      return 'DRAFT'
  }
}

/**
 * OpportunityService — lifecycle of commercial opportunities.
 */
export class OpportunityService {
  ensureLostReasons(): LostReason[] {
    const existing = readCrmJson<LostReason[]>(CRM_KEYS.lostReasons, 'lostReasons', [])
    if (existing.length > 0) return existing
    const seeded = DEFAULT_LOST_REASONS.map((reason) => ({
      ...reason,
      id: crypto.randomUUID(),
    }))
    writeCrmJson(CRM_KEYS.lostReasons, 'lostReasons', seeded)
    return seeded
  }

  listLostReasons(activeOnly = true): LostReason[] {
    const list = this.ensureLostReasons()
    return activeOnly ? list.filter((r) => r.active) : list
  }

  list(): Opportunity[] {
    return this.refreshIdle(this.rawList())
  }

  private rawList(): Opportunity[] {
    return readCrmJson<Opportunity[]>(CRM_KEYS.opportunities, 'opportunities', [])
  }

  getById(id: string): Opportunity | null {
    return this.list().find((o) => o.id === id) ?? null
  }

  getByQuoteId(quoteId: string): Opportunity | null {
    return this.list().find((o) => o.quoteId === quoteId) ?? null
  }

  upsertFromQuote(quote: QuoteDocument, responsibleUser = 'admin@masterhub.com'): Opportunity {
    const existing = this.getByQuoteId(quote.id)
    const customer = quote.customerId ? customerService.getById(quote.customerId) : null
    const stage = existing?.stage === 'NEGOTIATION' && quote.status === 'AWAITING'
      ? 'NEGOTIATION'
      : mapQuoteStatusToStage(quote.status)
    const now = nowIso()

    if (!existing) {
      const opportunity: Opportunity = {
        id: crypto.randomUUID(),
        quoteId: quote.id,
        quoteCode: quote.code,
        customerId: quote.customerId,
        customerName: quote.customerName ?? customer?.nome ?? null,
        customerPhone: customer?.whatsapp ?? customer?.telefone ?? null,
        customerCity: customer?.cidade ?? null,
        status: stage,
        stage,
        probability: STAGE_PROBABILITY[stage],
        expectedValue: quote.totals.total,
        expectedCloseDate: addDaysIso(now, quote.validUntilDays),
        lastContact: stage === 'DRAFT' ? null : now,
        nextContact: stage === 'DRAFT' || stage === 'APPROVED' || stage === 'LOST' || stage === 'CANCELLED'
          ? null
          : addDaysIso(now, 3),
        responsibleUser,
        notes: quote.notes ?? '',
        lostReasonId: null,
        lostReasonCode: null,
        lostReasonNotes: null,
        daysIdle: 0,
        createdAt: quote.createdAt || now,
        updatedAt: now,
      }
      writeCrmJson(CRM_KEYS.opportunities, 'opportunities', [opportunity, ...this.rawList()])
      this.pushTimeline(opportunity.id, 'created', 'Orçamento criado', opportunity.createdAt)
      if (stage !== 'DRAFT') {
        this.pushTimeline(opportunity.id, 'stage', STAGE_LABELS[stage], now)
      }
      return opportunity
    }

    const next: Opportunity = {
      ...existing,
      quoteCode: quote.code,
      customerId: quote.customerId,
      customerName: quote.customerName ?? customer?.nome ?? existing.customerName,
      customerPhone: customer?.whatsapp ?? customer?.telefone ?? existing.customerPhone,
      customerCity: customer?.cidade ?? existing.customerCity,
      expectedValue: quote.totals.total,
      stage: existing.stage === 'NEGOTIATION' && ['SENT', 'AWAITING'].includes(quote.status)
        ? 'NEGOTIATION'
        : mapQuoteStatusToStage(quote.status),
      status: existing.stage === 'NEGOTIATION' && ['SENT', 'AWAITING'].includes(quote.status)
        ? 'NEGOTIATION'
        : mapQuoteStatusToStage(quote.status),
      probability: STAGE_PROBABILITY[
        existing.stage === 'NEGOTIATION' && ['SENT', 'AWAITING'].includes(quote.status)
          ? 'NEGOTIATION'
          : mapQuoteStatusToStage(quote.status)
      ],
      updatedAt: now,
    }
    next.daysIdle = daysBetween(next.lastContact ?? next.updatedAt)
    this.replace(next)
    return next
  }

  syncFromQuotes(quotes: QuoteDocument[]): Opportunity[] {
    for (const quote of quotes) {
      this.upsertFromQuote(quote)
    }
    return this.list()
  }

  moveStage(id: string, stage: OpportunityStage, user = 'admin@masterhub.com'): Opportunity {
    const opportunity = this.getById(id)
    if (!opportunity) throw new Error('Oportunidade não encontrada')
    if (opportunity.stage === stage) return opportunity

    const now = nowIso()
    const next: Opportunity = {
      ...opportunity,
      stage,
      status: stage,
      probability: STAGE_PROBABILITY[stage],
      lastContact: now,
      nextContact:
        stage === 'APPROVED' || stage === 'LOST' || stage === 'CANCELLED'
          ? null
          : addDaysIso(now, 3),
      updatedAt: now,
      daysIdle: 0,
      responsibleUser: opportunity.responsibleUser || user,
    }
    this.replace(next)
    this.pushTimeline(id, 'stage', STAGE_LABELS[stage], now, user)
    return next
  }

  markLost(
    id: string,
    reasonCode: LostReasonCode,
    notes = '',
    user = 'admin@masterhub.com',
  ): Opportunity {
    const reasons = this.listLostReasons(false)
    const reason = reasons.find((r) => r.code === reasonCode) ?? null
    const opportunity = this.moveStage(id, 'LOST', user)
    const next: Opportunity = {
      ...opportunity,
      lostReasonId: reason?.id ?? null,
      lostReasonCode: reasonCode,
      lostReasonNotes: notes,
      updatedAt: nowIso(),
    }
    this.replace(next)
    this.pushTimeline(id, 'lost', reason?.description ?? reasonCode, nowIso(), notes)
    return next
  }

  recordContact(id: string, at = nowIso()): Opportunity {
    const opportunity = this.getById(id)
    if (!opportunity) throw new Error('Oportunidade não encontrada')
    const next: Opportunity = {
      ...opportunity,
      lastContact: at,
      nextContact: addDaysIso(at, 3),
      daysIdle: 0,
      updatedAt: at,
    }
    this.replace(next)
    this.pushTimeline(id, 'contact', 'Cliente lembrado / contato', at)
    return next
  }

  search(filters: OpportunityFilters): Opportunity[] {
    return this.list().filter((item) => {
      if (filters.status && filters.status !== 'ALL' && item.stage !== filters.status) return false
      if (filters.responsibleUser && !item.responsibleUser.toLowerCase().includes(filters.responsibleUser.toLowerCase())) {
        return false
      }
      if (filters.city && !(item.customerCity ?? '').toLowerCase().includes(filters.city.toLowerCase())) {
        return false
      }
      if (filters.customer && !(item.customerName ?? '').toLowerCase().includes(filters.customer.toLowerCase())) {
        return false
      }
      if (filters.periodFrom && item.createdAt < filters.periodFrom) return false
      if (filters.periodTo && item.createdAt > filters.periodTo) return false
      if (filters.query) {
        const q = filters.query.toLowerCase()
        const hay = `${item.customerName ?? ''} ${item.customerPhone ?? ''} ${item.quoteCode} ${item.customerCity ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }

  listTimeline(opportunityId: string): TimelineEvent[] {
    return readCrmJson<TimelineEvent[]>(CRM_KEYS.timeline, 'timeline', [])
      .filter((e) => e.opportunityId === opportunityId)
      .sort((a, b) => (a.at < b.at ? -1 : 1))
  }

  remove(id: string): void {
    writeCrmJson(
      CRM_KEYS.opportunities,
      'opportunities',
      this.rawList().filter((o) => o.id !== id),
    )
  }

  private replace(next: Opportunity): void {
    writeCrmJson(
      CRM_KEYS.opportunities,
      'opportunities',
      this.rawList().map((o) => (o.id === next.id ? next : o)),
    )
  }

  private refreshIdle(list: Opportunity[]): Opportunity[] {
    return list.map((item) => ({
      ...item,
      daysIdle: daysBetween(item.lastContact ?? item.updatedAt),
    }))
  }

  private pushTimeline(
    opportunityId: string,
    type: string,
    label: string,
    at: string,
    meta?: string | null,
  ): void {
    const entry: TimelineEvent = {
      id: crypto.randomUUID(),
      opportunityId,
      type,
      label,
      at,
      meta: meta ?? null,
    }
    const all = [entry, ...readCrmJson<TimelineEvent[]>(CRM_KEYS.timeline, 'timeline', [])].slice(0, 1000)
    writeCrmJson(CRM_KEYS.timeline, 'timeline', all)
  }
}

export const opportunityService = new OpportunityService()
